import React, { Component } from 'react';

import classNames from 'classnames';
import debounce from 'debounce-promise';
import PropTypes from 'prop-types';
import FaClose from 'react-icons/lib/fa/close';
import uuidV4 from 'uuid/v4';

import DatePicker from 'wanderu-component-datepicker';
import {
    Button,
    Input,
    InputDropdown,
    Layout,
    Options,
} from 'wanderu-components';
import { localize } from 'wanderu-i18n';
import GoogleService from 'wanderu-service-google';
import {
    formatAddressValue,
    generateFakeOnChangeEvent,
} from 'wanderu-utils';

import localesConfig from '../../../config/explorer.locales';

import styles from './Search.scss';
import childrenStyles from './SearchChildren.scss';

const DATEPICKER_PLACEHOLDER = 'any-day';

class Search extends Component {
    static propTypes = {
        clearClass: PropTypes.func,
        date: PropTypes.string,
        formattedAddress: PropTypes.string,
        i18nMoment: PropTypes.func,
        i18nMoney: PropTypes.func,
        input: PropTypes.string,
        locale: PropTypes.string.isRequired,
        onDateChange: PropTypes.func,
        onInputBlur: PropTypes.func,
        onInputChange: PropTypes.func,
        onInputFocus: PropTypes.func,
        onLocationChange: PropTypes.func,
        onPriceChange: PropTypes.func,
        onSearch: PropTypes.func,
        placeholder: PropTypes.string,
        price: PropTypes.number,
        t: PropTypes.func,
        type: PropTypes.string,
    };

    static defaultProps = {
        formattedAddress: '',
        onDateChange() { },
        onInputBlur() { },
        onInputChange() { },
        onLocationChange() { },
        onPriceChange() { },
        onSearch() { },
    };

    constructor(props) {
        super(props);

        this.state = {
            inputName: undefined,
            options: [],
            overlayVisible: false,
            sessiontoken: uuidV4(),
        };
        this.getPlacePredictions = debounce(this.getPlacePredictions, 175);
    }

    componentDidMount() {
        const {
            input,
        } = this.state;

        if (!input) {
            return;
        }

        this.getOptions(input);
    }

    async getPlacePredictions(input, locale) {
        const { sessiontoken } = this.state;
        return GoogleService.getPlacePredictions(input, {
            locale,
            sessiontoken,
        });
    }

    async getOptions(input) {
        const {
            locale,
        } = this.props;

        if (!input) {
            return this.setState(() => ({ options: [] }));
        }

        try {
            const predictions = await this.getPlacePredictions(input, locale);
            this.setState(() => ({ options: predictions }));
        } catch (ex) {
            this.setState(() => ({ options: [] }));
        }
        return undefined;
    }

    get priceOptions() {
        const { t } = this.props;

        return [
            {
                text: t('any-price'),
                value: undefined,
            },
            {
                text: t('under-price', { price: this.formatPrice(10) }),
                value: 10,
            },
            {
                text: t('under-price', { price: this.formatPrice(20) }),
                value: 20,
            },
            {
                text: t('under-price', { price: this.formatPrice(30) }),
                value: 30,
            },
            {
                text: t('under-price', { price: this.formatPrice(40) }),
                value: 40,
            },
            {
                text: t('under-price', { price: this.formatPrice(50) }),
                value: 50,
            },
        ];
    }

    formatPrice(price) {
        const { i18nMoney } = this.props;

        return i18nMoney(price).format({
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
        });
    }

    get currentPriceFilter() {
        const { price } = this.props;
        const priceOption = this.priceOptions
            .find((option) => price === option.value);

        return priceOption.text;
    }

    onDateInputFocus = (e) => {
        this.setState(() => ({
            inputName: 'datePicker',
            overlayVisible: true,
        }));
    };

    onPriceInputFocus = (e) => {
        this.setState(() => ({
            inputName: 'priceInputDropdown',
            overlayVisible: true,
        }));
    };

    onLocationInputFocus = (e) => {
        this.setState(() => ({
            inputName: 'locationInputDropdown',
            overlayVisible: true,
        }));

        this.props.onInputFocus(e);
    };

    onInputChange = (e) => {
        const input = e.target.value;
        this.props.onInputChange(e);
        this.getOptions(input);
    };

    onInputBlur = (e) => {
        this.setState(() => ({ overlayVisible: false }));
        this.props.onInputBlur(e);
    };

    onLocationInputChange = (e) => {
        const option = e.target.value || {};
        const {
            placeId,
            text,
        } = option;

        const fakeEvent = generateFakeOnChangeEvent(e, placeId);
        this.props.onLocationChange(fakeEvent);

        const fakeInputEvent = generateFakeOnChangeEvent(e, text);
        this.props.onInputChange(fakeInputEvent);

        this.setState(() => ({
            overlayVisible: false,
            sessiontoken: uuidV4(),
        }));
    };

    onDateChange = (e) => {
        let date = e.target.value;
        if (date) {
            date = date.format('YYYY-MM-DD');
        }

        this.setState(() => ({
            date,
            overlayVisible: false,
        }));

        this.props.onDateChange(e);
    };

    onDateRemove = (e) => {
        const fakeEvent = generateFakeOnChangeEvent(e, undefined);
        this.props.onDateChange(fakeEvent);

        this.setState(() => ({
            date: undefined,
            overlayVisible: false,
        }));
    };

    onPriceChange = (e) => {
        const option = e.target.value;
        const price = option.value;

        const fakeEvent = generateFakeOnChangeEvent(e, price);
        this.props.onPriceChange(fakeEvent);

        this.setState(() => ({
            overlayVisible: false,
            price,
        }));
    };

    onOverlayClick = (e) => {
        this.setState(() => ({ overlayVisible: false }));
    };

    inputIsFocused(currentInputName) {
        const { overlayVisible, inputName } = this.state;
        return overlayVisible && inputName === currentInputName;
    }

    render() {
        const {
            overlayVisible,
        } = this.state;
        const {
            clearClass,
            i18nMoment,
            input,
            locale,
            onSearch,
            placeholder,
            t,
            type,
        } = this.props;

        let date = this.props.date;
        if (date) {
            date = i18nMoment(this.props.date);
        }

        return (
            <Layout className={styles.container}>
                <div
                    className={classNames(
                        styles.overlay,
                        { [styles.visible]: overlayVisible },
                    )}
                    onClick={this.onOverlayClick}
                />
                <div className={styles.inputContainer}>
                    <span className={styles.label}>
                        {t('from')}
                    </span>
                    <span
                        className={classNames(
                            styles.flexShrink,
                            { [styles.focused]: this.inputIsFocused('locationInputDropdown') },
                        )}
                    >
                        <InputDropdown
                            defaultValue={this.props.formattedAddress}
                            onBlur={this.onInputBlur}
                            onChange={this.onLocationInputChange}
                            onFocus={this.onLocationInputFocus}
                            onInputChange={this.onInputChange}
                            options={this.state.options}
                            reactInputComponent={Input}
                            value={formatAddressValue(input)}
                        >
                            <Input
                                clearClass={clearClass}
                                inputClass={childrenStyles.inputClass}
                                placeholder={placeholder}
                                textClass={childrenStyles.inputText}
                                type={type}
                                autoClear
                                autoFocus
                                required
                                underline
                            />
                            <Options
                                className={childrenStyles.options}
                                poweredByGoogle
                                showOptionsCaret
                                substringMatch
                            />
                        </InputDropdown>
                    </span>
                </div>
                <div className={styles.inputContainer}>
                    <span className={styles.label}>
                        {t('on')}
                    </span>
                    <span
                        className={classNames(
                            styles.flexShrink,
                            childrenStyles.dateContainer,
                            { [styles.focused]: this.inputIsFocused('datePicker') },
                        )}
                    >
                        <DatePicker
                            calendarClass={childrenStyles.calendarContainer}
                            currentDate={date}
                            inputClass={classNames(
                                childrenStyles.datePickerInput,
                                { [childrenStyles.datePickerInputFixedWidth]: !!date },
                            )}
                            locale={locale}
                            maxDate={i18nMoment().add(30, 'days')}
                            minDate={i18nMoment()}
                            onBlur={this.onInputBlur}
                            onChange={this.onDateChange}
                            onFocus={this.onDateInputFocus}
                            placeholder={t(DATEPICKER_PLACEHOLDER)}
                            textClass={classNames(childrenStyles.inputText)}
                            underline
                        />
                        {date && (
                            <FaClose
                                className={styles.remove}
                                onClick={this.onDateRemove}
                            />
                        )}
                    </span>
                </div>
                <div className={styles.inputContainer}>
                    <span className={styles.label}>
                        {t('for')}
                    </span>
                    <span
                        className={classNames(
                            styles.flexShrink,
                            { [styles.focused]: this.inputIsFocused('priceInputDropdown') },
                        )}
                    >
                        <InputDropdown
                            onBlur={this.onInputBlur}
                            onChange={this.onPriceChange}
                            onFocus={this.onPriceInputFocus}
                            options={this.priceOptions}
                            reactInputComponent={Input}
                            value={this.currentPriceFilter}
                        >
                            <Input
                                inputClass={childrenStyles.inputClass}
                                placeholder={this.currentPriceFilter}
                                textClass={childrenStyles.inputText}
                                type={type}
                                readOnly
                                underline
                            />
                            <Options
                                className={childrenStyles.options}
                                optionContainerClass={childrenStyles.optionContainer}
                                substringMatch={false}
                                showOptionsCaret
                            />
                        </InputDropdown>
                    </span>
                </div>
                <div className={styles.inputContainer}>
                    <Button
                        className={styles.button}
                        onClick={onSearch}
                    >
                        {t('alright-lets-wanderu')}
                    </Button>
                </div>
            </Layout>
        );
    }
}

export { Search };
export default localize(Search, localesConfig.namespace);
